import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  completeMarketplaceEmailVerification,
  requestMarketplaceEmailVerification,
} from "../api/marketplaceAuth";
import { OPS_HOST, buildHostUrl } from "../config/hostRouting";

function formatIndependentProfessionalId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^IP-?0*(\d+)$/i);
  if (match) return match[1] || "0";
  return raw;
}

function getIndependentProfessionalNumber(data) {
  const explicitNumber =
    data?.independent_operator_number ??
    data?.renter_profile?.independent_operator_number;
  if (explicitNumber !== undefined && explicitNumber !== null && explicitNumber !== "") {
    return String(explicitNumber);
  }
  return formatIndependentProfessionalId(
    data?.independent_operator_id ?? data?.renter_profile?.independent_operator_id
  );
}

export default function MarketplaceVerifyEmail() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const token = (searchParams.get("token") || "").trim();
  const initialEmail =
    (searchParams.get("email") || location.state?.email || "").trim();

  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(Boolean(token));
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState(
    token
      ? "Verifying your email..."
      : "Check your inbox for the verification link, or resend it below."
  );

  const successDestination = "/login";

  useEffect(() => {
    let active = true;
    if (!token) return undefined;
    completeMarketplaceEmailVerification(token)
      .then((data) => {
        if (!active) return;
        if (data?.access_token) {
          login(data.access_token, data);
        }
        const professionalId = getIndependentProfessionalNumber(data);
        const params = new URLSearchParams();
        if (professionalId) {
          params.set("professionalId", professionalId);
        }
        if ((data?.email || initialEmail).trim()) {
          params.set("email", (data?.email || initialEmail).trim());
        }
        params.set("next", "/setup?mode=independent_professional");
        window.location.assign(
          buildHostUrl(OPS_HOST, successDestination, `?${params.toString()}`)
        );
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "Unable to verify your email.");
        setMessage("");
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [initialEmail, login, successDestination, token]);

  const handleResend = async (event) => {
    event.preventDefault();
    setError("");
    setResending(true);
    try {
      const result = await requestMarketplaceEmailVerification(email.trim());
      setMessage(result?.message || "If an account matches, we emailed a verification link.");
    } catch (err) {
      setError(err.message || "Unable to resend the verification email.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#fef3f2,_#f7ebe7_42%,_#eef2ff_100%)] px-4 py-8">
      <div className="mx-auto max-w-3xl rounded-[28px] border border-white/70 bg-white/80 p-6 shadow-[0_20px_70px_rgba(47,53,102,0.12)] backdrop-blur sm:p-8">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">
          MyBoothShare verification
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
          Verify your email
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
          Your MyBoothShare account uses the same shared Ellyanna identity underneath.
          Verify your email once, then continue in Ellyanna Booking to finish
          Independent Professional setup and prepare billing.
        </p>

        {message ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        {!token ? (
          <form onSubmit={handleResend} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="marketplace-verify-email"
                className="block text-xs font-medium uppercase tracking-wide text-slate-700"
              >
                Email
              </label>
              <input
                id="marketplace-verify-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
                placeholder="you@example.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={resending}
              className="w-full rounded-xl bg-rose-500 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {resending ? "Sending..." : "Resend verification email"}
            </button>
          </form>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-3 text-sm">
          <Link
            to="/login"
            className="inline-flex items-center justify-center rounded-full border border-[#2f3566]/20 bg-white px-4 py-2 font-semibold text-[#2f3566] transition hover:bg-[#2f3566]/5"
          >
            Back to login
          </Link>
          <Link
            to="/search"
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Browse workspaces
          </Link>
        </div>

        {loading ? (
          <div className="mt-6 text-sm text-slate-500">Finishing verification...</div>
        ) : null}
      </div>
    </div>
  );
}
